import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AnimatedTabs } from "@/components/AnimatedTabs";
import { StatCounter } from "@/components/StatCounter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { IMAGES } from "@/assets/images";
import { SERVICES_DATA, ONGOING_PROJECTS, COMPLETED_PROJECTS, FLEET_EQUIPMENT, FLEET_PERSONNEL, CERTIFICATIONS_DATA, AWARDS_DATA, MILESTONES_DATA, REGISTRATIONS_DATA, CONTACT_INFO, EMERGENCY_CONTACTS, STATS } from "@/lib/index";
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { t, Lang } from "@/lib/translations";
import { Drill, Route, Mountain, Pipette, Wind, Truck, Droplets, Settings, MapPin, Phone, Mail, Building2, Award, Calendar, CheckCircle2, AlertCircle, Tractor, Container, ArrowLeftRight, Shovel, CircleDot, Gauge, HardHat, ShieldCheck, Compass, ClipboardCheck, UserCog } from "lucide-react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Drill,
  Route,
  Mountain,
  Pipe: Pipette,
  Wind,
  Truck,
  Droplet: Droplets,
  Settings,
};

const SLIDE_IMAGES = [
  "/images/Screenshot 2026-05-10 172730.png",
  "/images/Screenshot 2026-05-07 111612.png",
  "/images/Screenshot 2026-05-07 112202.png",
  "/images/Screenshot 2026-05-07 111446.png",
  "/images/Screenshot 2026-05-07 112239.png",
  "/images/Screenshot 2026-05-07 085042.png",
  "/images/desert-oil-rig-stockcake.jpg",
];

// Slide stays 4 seconds — fast like a video reel
const SLIDE_DURATION = 4000;
// Crossfade 0.8s — quick but smooth dissolve
const FADE_DURATION = 0.8;
// Ken Burns zoom over full slide duration
const KB_DURATION = 5;

function VideoHeader() {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setPrev((p) => (p === null ? 0 : active));
      setActive((cur) => (cur + 1) % SLIDE_IMAGES.length);
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const goTo = (idx: number) => {
    if (idx === active) return;
    setPrev(active);
    setActive(idx);
  };

  return (
    <div className="absolute inset-0 z-[1] overflow-hidden bg-black">
      {SLIDE_IMAGES.map((src, i) => {
        const isActive = i === active;
        const isPrev = i === prev;
        if (!isActive && !isPrev) return null;
        return (
          <motion.div
            key={i}
            className="absolute inset-0"
            initial={false}
            animate={{ opacity: isActive ? 1 : 0 }}
            transition={{ duration: FADE_DURATION, ease: "easeInOut" }}
            style={{ zIndex: isActive ? 2 : 1 }}
          >
            <motion.img
              src={src}
              alt={`Slide ${i + 1}`}
              className="w-full h-full object-cover"
              initial={{ scale: 1.12 }}
              animate={isActive ? { scale: 1.0 } : { scale: 1.12 }}
              transition={{ duration: KB_DURATION, ease: "linear" }}
            />
          </motion.div>
        );
      })}

      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />

      {/* Caption removed as requested */}

      {/* Progress bar — like a video progress indicator */}
      <div className="absolute bottom-8 left-8 right-8 z-20 flex items-center gap-3">
        {SLIDE_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
            className="relative flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden group cursor-pointer"
          >
            {i === active && (
              <motion.span
                className="absolute inset-y-0 left-0 bg-primary rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: SLIDE_DURATION / 1000, ease: 'linear' }}
              />
            )}
            {i < active && (
              <span className="absolute inset-0 bg-primary/70 rounded-full" />
            )}
            <span className="absolute inset-0 bg-white/0 group-hover:bg-white/20 transition-colors rounded-full" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Org Chart helpers ──────────────────────────────────────────── */
type OrgLevel = "ceo" | "exec" | "dept" | "sub" | "field" | "worker";

function OrgCard({ label, level }: { label: string; level: OrgLevel }) {
  const wrap: Record<OrgLevel, string> = {
    ceo:    "flex flex-col items-center gap-1.5 px-5 py-3 rounded-2xl border-2 border-primary bg-primary/10 shadow-md min-w-[130px] max-w-[180px]",
    exec:   "flex flex-col items-center gap-1.5 px-4 py-2.5 rounded-2xl border border-border bg-card shadow min-w-[120px] max-w-[170px]",
    dept:   "flex flex-col items-center gap-1 px-3 py-2 rounded-xl border border-border bg-card shadow min-w-[100px] max-w-[150px]",
    sub:    "flex flex-col items-center gap-1 px-3 py-2 rounded-xl border border-border bg-muted/50 min-w-[90px] max-w-[140px]",
    field:  "flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl border border-border bg-muted/40 min-w-[90px] max-w-[140px]",
    worker: "flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl border border-border bg-muted/30 min-w-[90px] max-w-[140px]",
  };
  const iconWrap: Record<OrgLevel, string> = {
    ceo:    "bg-primary/20 text-primary rounded-xl p-2",
    exec:   "bg-muted text-muted-foreground rounded-xl p-2",
    dept:   "bg-muted text-muted-foreground rounded-xl p-1.5",
    sub:    "bg-muted/70 text-muted-foreground rounded-xl p-1.5",
    field:  "bg-muted/50 text-muted-foreground rounded-lg p-1.5",
    worker: "bg-muted/40 text-muted-foreground rounded-lg p-1.5",
  };
  const textSize: Record<OrgLevel, string> = {
    ceo:    "text-[11px] font-bold text-primary uppercase tracking-wide text-center leading-tight",
    exec:   "text-[10px] font-semibold text-foreground uppercase tracking-wide text-center leading-tight",
    dept:   "text-[10px] font-semibold text-foreground uppercase tracking-wide text-center leading-tight",
    sub:    "text-[9px] font-semibold text-muted-foreground uppercase tracking-wide text-center leading-tight",
    field:  "text-[9px] font-semibold text-muted-foreground uppercase tracking-wide text-center leading-tight",
    worker: "text-[9px] font-medium text-muted-foreground uppercase tracking-wide text-center leading-tight",
  };
  const iconSize: Record<OrgLevel, number> = { ceo: 22, exec: 18, dept: 16, sub: 14, field: 14, worker: 14 };
  const sz = iconSize[level];
  return (
    <div className={wrap[level]}>
      <div className={iconWrap[level]}>
        <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
      </div>
      <span className={textSize[level]}>{label}</span>
    </div>
  );
}

function VLine({ h = 28 }: { h?: number }) {
  return <div style={{ width: 1, height: h }} className="bg-border/60 flex-shrink-0" />;
}
/* ──────────────────────────────────────────────────────────────── */

function AnimatedSection({ children, id }: { children: React.ReactNode; id?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className="py-24"
    >
      {children}
    </motion.section>
  );
}

function ContactForm({ isAr }: { isAr: boolean }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', website: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', phone: '', website: '', message: '' });
  };

  const inputClass = 'w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <input
          type="text"
          placeholder={isAr ? 'الاسم' : 'Name'}
          value={formData.name}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
          className={inputClass}
          required
        />
        <input
          type="email"
          placeholder={isAr ? 'البريد الإلكتروني' : 'Email'}
          value={formData.email}
          onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
          className={inputClass}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="tel"
          placeholder={isAr ? 'رقم الهاتف' : 'Phone Number'}
          value={formData.phone}
          onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
          className={inputClass}
        />
        <input
          type="url"
          placeholder={isAr ? 'موقعك الإلكتروني' : 'Your Website'}
          value={formData.website}
          onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
          className={inputClass}
        />
      </div>
      <textarea
        rows={5}
        placeholder={isAr ? 'رسالتك هنا...' : 'Your Message Here'}
        value={formData.message}
        onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
        className={`${inputClass} resize-y`}
        required
      />
      {submitted ? (
        <div className="flex items-center gap-2 text-green-600 font-semibold text-sm py-2">
          <CheckCircle2 className="w-5 h-5" />
          {isAr ? 'تم إرسال رسالتك بنجاح!' : 'Your message has been sent successfully!'}
        </div>
      ) : (
        <button
          type="submit"
          className="w-auto px-8 py-3 bg-primary text-white font-extrabold uppercase tracking-widest hover:bg-primary/90 transition-all duration-200 shadow-md hover:shadow-lg"
          style={{ clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)' }}
        >
          {isAr ? 'أرسل الآن' : 'SUBMIT NOW'}
        </button>
      )}
    </form>
  );
}

export default function Home() {
  const [lang, setLang] = useState<Lang>('en');
  const T = t[lang];
  const isAr = lang === 'ar';

  const serviceTabs = SERVICES_DATA.map((service) => {
    const IconComponent = iconMap[service.icon];
    return {
      id: service.id,
      label: service.title,
      icon: IconComponent ? <IconComponent className="w-5 h-5" /> : null,
      content: (
        <Card className="border-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              {IconComponent && (
                <div className="p-3 rounded-lg bg-primary/10">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>
              )}
              <CardTitle className="text-2xl">{service.title}</CardTitle>
            </div>
            <CardDescription className="text-base leading-relaxed">
              {service.description}
            </CardDescription>
          </CardHeader>
        </Card>
      ),
    };
  });

  const projectTabs = [
    {
      id: "ongoing",
      label: isAr ? `▶  ${T.projects.ongoing}` : "▶  ONGOING CONTRACTS",
      content: (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{T.projects.period}</TableHead>
                <TableHead>{T.projects.type}</TableHead>
                <TableHead>{isAr ? 'الوصف' : 'DESCRIPTION'}</TableHead>
                <TableHead>{T.projects.location}</TableHead>
                <TableHead>{T.projects.client}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ONGOING_PROJECTS.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-semibold text-primary">{project.period}</TableCell>
                  <TableCell>
                    <Badge className="bg-primary/20 text-primary border border-primary/30">{project.type}</Badge>
                  </TableCell>
                  <TableCell className="max-w-sm">{project.description}</TableCell>
                  <TableCell>{project.location}</TableCell>
                  <TableCell className="text-primary font-semibold">{project.client}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ),
    },
    {
      id: "completed",
      label: isAr ? `✓  ${T.projects.completed}` : "✓  COMPLETED PROJECTS",
      content: (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{T.projects.period}</TableHead>
                <TableHead>{T.projects.type}</TableHead>
                <TableHead>{isAr ? 'الوصف' : 'DESCRIPTION'}</TableHead>
                <TableHead>{T.projects.location}</TableHead>
                <TableHead>{T.projects.client}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {COMPLETED_PROJECTS.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-semibold text-primary">{project.period}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border">{project.type}</Badge>
                  </TableCell>
                  <TableCell className="max-w-sm">{project.description}</TableCell>
                  <TableCell>{project.location}</TableCell>
                  <TableCell className="text-primary font-semibold">{project.client}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ),
    },
  ];

  const certificationTabs = [
    {
      id: "iso",
      label: isAr ? "شهادات ISO" : "ISO Certifications",
      content: (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="grid md:grid-cols-2 gap-6">
          {CERTIFICATIONS_DATA.map((cert) => (
            <motion.div key={cert.id} variants={staggerItem}>
              <Card className="border-border bg-card h-full">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl mb-2">{cert.name}</CardTitle>
                      <CardDescription className="text-sm">{cert.issuer}</CardDescription>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{isAr ? 'رقم الشهادة:' : 'Cert No:'}</span>
                    <span className="font-semibold font-mono text-xs">{cert.certificateNumber}</span>
                  </div>
                  {cert.expiryDate && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{isAr ? 'صالح حتى:' : 'Valid Until:'}</span>
                      <span className="font-semibold">{cert.expiryDate}</span>
                    </div>
                  )}
                  {cert.scope && <p className="text-sm text-muted-foreground pt-2">{cert.scope}</p>}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ),
    },
    {
      id: "awards",
      label: isAr ? "جوائز أرامكو" : "Saudi Aramco Awards",
      content: (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-4">
          {AWARDS_DATA.map((award) => (
            <motion.div key={award.id} variants={staggerItem}>
              <Card className="border-border bg-card">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10 flex-shrink-0">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <CardTitle className="text-lg">{award.title}</CardTitle>
                        <Badge className="bg-primary/20 text-primary border-primary/30">{award.date}</Badge>
                      </div>
                      <CardDescription className="text-sm mb-2">{award.issuer}</CardDescription>
                      <p className="text-sm text-muted-foreground">{award.description}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ),
    },
    {
      id: "registrations",
      label: isAr ? "التسجيلات" : "Registrations",
      content: (
        <div className="space-y-4">
          {REGISTRATIONS_DATA.map((reg) => (
            <Card key={reg.id} className="border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{reg.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{reg.number}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ),
    },
  ];

  const missionVisionValues = isAr
    ? [
        { label: "رسالتنا", text: T.about.missionText },
        { label: "رؤيتنا", text: T.about.visionText },
        { label: "قيمنا", items: ["النزاهة", "الالتزام بالسلامة والصحة والبيئة", "التركيز على العميل", "تطوير الكوادر", "الموقف الإيجابي", "العمل الجماعي"] },
      ]
    : [
        { label: "OUR MISSION", text: "To significantly contribute to the Civil Work industry and to preserve the environment through optimum quality and safety, working according to our core values." },
        { label: "OUR VISION", text: "To create local opportunity, growth, and impact in every community where we operate. To maintain global leadership in chosen areas of business." },
        { label: "OUR VALUES", items: ["Integrity", "Committed to HSE", "Customer Focus", "People Development", "Positive Attitude", "Teamwork"] },
      ];

  const personnelItems = [
    { count: FLEET_PERSONNEL.engineers, labelEn: "Engineers", labelAr: "مهندسون", icon: HardHat },
    { count: FLEET_PERSONNEL.hseTeam, labelEn: "HSE Team", labelAr: "فريق السلامة", icon: ShieldCheck },
    { count: FLEET_PERSONNEL.surveyors, labelEn: "Surveyors", labelAr: "مساحون", icon: Compass },
    { count: FLEET_PERSONNEL.workPermitReceivers, labelEn: "Work Permit Receivers", labelAr: "مستلمو تصاريح العمل", icon: ClipboardCheck },
    { count: FLEET_PERSONNEL.fieldSupervisors, labelEn: "Field Supervisors", labelAr: "مشرفو الميدان", icon: UserCog },
  ];

  const regTableRows = isAr
    ? [
        [T.contact.crNumber, CONTACT_INFO.cr],
        [T.contact.vendorNo, CONTACT_INFO.vendorNo],
        [T.contact.vatReg, CONTACT_INFO.vat],
        [T.contact.scaMembership, CONTACT_INFO.sca],
        [T.contact.poBox, CONTACT_INFO.poBox],
      ]
    : [
        ["CR Number", CONTACT_INFO.cr],
        ["Vendor No. (Aramco)", CONTACT_INFO.vendorNo],
        ["VAT Registration", CONTACT_INFO.vat],
        ["SCA Membership", CONTACT_INFO.sca],
        ["P.O. Box", CONTACT_INFO.poBox],
      ];

  return (
    <Layout lang={lang} setLang={setLang}>
      {/* ═══ HERO ═══ */}
      <section id="hero" className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <VideoHeader />
        <div className="absolute inset-0 z-[2] bg-gradient-to-b from-background/75 via-background/50 to-background/85 pointer-events-none" />

        <div className={`container mx-auto px-4 relative z-[3] max-w-5xl ${isAr ? 'text-right' : 'text-left'}`}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className={`text-primary text-sm uppercase tracking-[0.3em] flex items-center gap-3 pt-10 pb-10 font-bold text-center xl:text-base ${isAr ? 'flex-row-reverse justify-end' : ''}`}
            >
              <span className="inline-block w-10 h-px bg-primary"></span>
              {isAr ? 'المملكة العربية السعودية · تأسست 2008' : 'KINGDOM OF SAUDI ARABIA · EST. 2008'}
            </motion.p>

            <div>
              <h1 className="text-6xl md:text-8xl font-extrabold tracking-tight leading-none text-primary">
                {T.hero.name1}
              </h1>
              <h1 className="text-6xl md:text-8xl font-extrabold tracking-tight leading-none" style={{ color: '#0a0a0a' }}>
                {T.hero.name2}
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold tracking-widest mt-2" style={{ color: '#1a1a1a' }}>
                {T.hero.name3}
              </h2>
            </div>

            <p className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {T.hero.subtitle}
            </p>

            <div className={`flex flex-wrap gap-4 pt-4 ${isAr ? 'flex-row-reverse' : ''}`}>
              <a
                href="#projects"
                className="inline-flex items-center px-8 py-3 bg-primary text-white font-extrabold uppercase tracking-widest text-sm shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all duration-200"
                style={{ clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)' }}
              >
                {T.hero.viewProjects}
              </a>
              <a
                href="#contact"
                className="inline-flex items-center px-8 py-3 bg-transparent text-primary font-extrabold uppercase tracking-widest text-sm border-2 border-primary hover:bg-primary hover:text-white transition-all duration-200"
                style={{ clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)' }}
              >
                {T.hero.contactUs}
              </a>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-border/40"
            >
              {STATS.map((stat) => (
                <div key={stat.id} className="space-y-1">
                  {stat.id === 'stat-1' ? (
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-bold text-primary mb-2">2008</div>
                      <div className="text-sm md:text-base text-muted-foreground uppercase tracking-wider">{T.hero.stats.founded}</div>
                    </div>
                  ) : (
                    <StatCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      label={
                        stat.id === 'stat-2' ? T.hero.stats.experience :
                        stat.id === 'stat-3' ? T.hero.stats.team :
                        T.hero.stats.machines
                      }
                      duration={2200}
                    />
                  )}
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[3] flex flex-col items-center gap-2 text-muted-foreground text-xs tracking-widest uppercase"
        >
          <span>{isAr ? 'انزل' : 'SCROLL'}</span>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-px h-8 bg-gradient-to-b from-primary to-transparent" />
        </motion.div>
      </section>

      {/* ═══ ABOUT ═══ */}
      <AnimatedSection id="about">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className={`max-w-3xl mb-16 ${isAr ? 'text-right' : ''}`}>
            <p className={`text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.about.badge}
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 uppercase tracking-tight">
              {isAr ? 'مبنيون على القوة،\nمدفوعون بالهدف' : 'Built on Strength,\nDriven by Purpose'}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{T.about.p1}</p>
            <p className="text-lg text-muted-foreground leading-relaxed mt-4">{T.about.p2}</p>
          </motion.div>

          <motion.div variants={fadeInUp} className="mb-20">
            <Card className="border-primary/20 bg-card/50">
              <CardContent className="p-8 md:p-12">
                <div className={`flex items-start gap-6 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="text-7xl text-primary leading-none font-serif">{isAr ? '»' : '«'}</div>
                  <div>
                    <p className="text-xl md:text-2xl text-foreground leading-relaxed mb-6">{T.about.ceoQuote}</p>
                    <div className={`flex items-center gap-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                      <div className="w-12 h-px bg-primary"></div>
                      <div>
                        <p className="font-bold text-foreground">Hamad Al Mutairi</p>
                        <p className="text-sm text-muted-foreground">{T.about.ceoName}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6 mb-20">
            {missionVisionValues.map((item) => (
              <motion.div key={item.label} variants={staggerItem}>
                <Card className={`border-border bg-card h-full hover:border-primary/40 transition-colors duration-300 ${isAr ? 'text-right' : ''}`}>
                  <CardHeader>
                    <p className="text-primary text-xs font-bold uppercase tracking-[0.25em] mb-2">{item.label}</p>
                  </CardHeader>
                  <CardContent>
                    {item.text ? (
                      <p className="text-muted-foreground leading-relaxed">{item.text}</p>
                    ) : (
                      <ul className="space-y-2">
                        {item.items?.map((v, i) => (
                          <li key={v} className={`flex items-center gap-3 text-muted-foreground ${isAr ? 'flex-row-reverse' : ''}`}>
                            <span className="text-primary font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                            <span>{v}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <div>
            <h3 className="text-2xl font-bold text-center mb-12 uppercase tracking-widest text-primary">{T.about.milestones}</h3>
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gradient-to-b from-primary via-primary/40 to-transparent" />
              <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-10">
                {MILESTONES_DATA.map((milestone, index) => (
                  <motion.div key={milestone.id} variants={staggerItem} className={`flex items-center gap-6 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? "text-right" : "text-left"}`}>
                      <Card className={`border-border bg-card inline-block max-w-sm hover:border-primary/40 transition-colors duration-300 ${isAr ? 'text-right' : ''}`}>
                        <CardHeader className="pb-2">
                          <p className="text-primary font-extrabold text-2xl">{milestone.year}</p>
                          <CardTitle className="text-base">{milestone.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">{milestone.description}</p>
                        </CardContent>
                      </Card>
                    </div>
                    <div className="w-4 h-4 rounded-full bg-primary ring-4 ring-primary/20 flex-shrink-0 z-10" />
                    <div className="flex-1" />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══ SERVICES ═══ */}
      <AnimatedSection id="services">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.services.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">{T.services.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{T.services.subtitle}</p>
          </motion.div>
          <AnimatedTabs tabs={serviceTabs} variant="underline" />
        </div>
      </AnimatedSection>

      {/* ═══ PROJECTS ═══ */}
      <AnimatedSection id="projects">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.projects.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">{T.projects.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{T.projects.subtitle}</p>
          </motion.div>
          <AnimatedTabs tabs={projectTabs} variant="pill" />
        </div>
      </AnimatedSection>

      {/* ═══ FLEET ═══ */}
      <AnimatedSection id="fleet">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.fleet.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">{T.fleet.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{T.fleet.subtitle}</p>
          </motion.div>

          <h3 className={`text-xl font-bold mb-6 uppercase tracking-wider text-muted-foreground ${isAr ? 'text-right' : ''}`}>{T.fleet.equipment}</h3>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
            {FLEET_EQUIPMENT.map((item) => {
              const equipIcons: Record<string, React.ComponentType<{ className?: string }>> = {
                'eq-1': Tractor, 'eq-2': Container, 'eq-3': Gauge, 'eq-4': Shovel,
                'eq-5': CircleDot, 'eq-6': Truck, 'eq-7': ArrowLeftRight, 'eq-8': Truck, 'eq-9': Droplets, 'eq-10': Droplets,
              };
              const EquipIcon = equipIcons[item.id];
              return (
                <motion.div key={item.id} variants={staggerItem} whileHover={{ scale: 1.04 }} transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                  <Card className="border-border bg-card text-center h-full hover:border-primary/60 transition-colors duration-300">
                    <CardContent className="pt-6 pb-5">
                      <div className="flex items-center justify-center mb-3">
                        <div className="p-3 rounded-xl bg-primary/10">
                          {EquipIcon && <EquipIcon className="w-8 h-8 text-primary" />}
                        </div>
                      </div>
                      <p className="text-3xl font-extrabold text-primary mb-1">{item.count}</p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{item.name}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          <h3 className={`text-xl font-bold mb-6 uppercase tracking-wider text-muted-foreground ${isAr ? 'text-right' : ''}`}>{T.fleet.personnel}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {personnelItems.map((p) => {
              const PIcon = p.icon;
              return (
                <Card key={p.labelEn} className="border-border bg-card text-center hover:border-primary/60 transition-colors duration-300">
                  <CardContent className="pt-6 pb-5">
                    <div className="flex items-center justify-center mb-3">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <PIcon className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                    <p className="text-3xl font-extrabold text-primary mb-1">{p.count}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{isAr ? p.labelAr : p.labelEn}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══ CERTIFICATIONS ═══ */}
      <AnimatedSection id="certifications">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.certifications.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">{T.certifications.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{T.certifications.subtitle}</p>
          </motion.div>
          <AnimatedTabs tabs={certificationTabs} variant="gradient-pill" />
        </div>
      </AnimatedSection>

      {/* ═══ ORG CHART ═══ */}
      <AnimatedSection id="org-chart">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.org.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight">{T.org.title}</h2>
          </motion.div>

          <div className="w-full overflow-x-auto pb-4">
            <div className="min-w-[900px] flex flex-col items-center">
              <OrgCard label={isAr ? "الرئيس التنفيذي / المدير العام" : "CEO / President"} level="ceo" />
              <VLine h={32} />
              <OrgCard label={isAr ? "السكرتير التنفيذي" : "Executive Secretary"} level="exec" />
              <VLine h={32} />
              <OrgCard label={isAr ? "المدير العام" : "General Manager"} level="dept" />
              <VLine h={20} />

              <div className="relative w-full flex justify-center">
                <div className="absolute top-0 h-px bg-border/60" style={{ left: '13%', right: '13%' }} />
                <div className="flex gap-16 justify-center">
                  {(isAr
                    ? [
                        { label: "مشرف السلامة", sub: "فريق السلامة" },
                        { label: "مشرف الجودة", sub: "منسق المشروع" },
                        { label: "مدير العمليات", sub: "مهندس التكاليف" },
                        { label: "المالية والإدارة", sub: "محاسب" },
                      ]
                    : [
                        { label: "HSE Supervisor", sub: "HSE Team" },
                        { label: "QA/QC Supervisor", sub: "Project Coordinator" },
                        { label: "Operation Manager", sub: "Cost Engineer" },
                        { label: "Finance & Admin", sub: "Accountant" },
                      ]
                  ).map((dept) => (
                    <div key={dept.label} className="flex flex-col items-center">
                      <VLine h={20} />
                      <OrgCard label={dept.label} level="dept" />
                      <VLine h={20} />
                      <OrgCard label={dept.sub} level="sub" />
                    </div>
                  ))}
                </div>
              </div>

              <VLine h={32} />

              <div className="relative w-full flex justify-center">
                <div className="absolute top-0 h-px bg-border/50" style={{ left: '5%', right: '5%' }} />
                <div className="flex gap-10 justify-center">
                  {(isAr
                    ? ["مدير الإنشاء", "مدير المشروع", "مهندسو المشاريع", "المساحون", "المراقبون"]
                    : ["Construction Manager", "Project Manager", "Project Engineers", "Surveyors", "Foremen"]
                  ).map((role) => (
                    <div key={role} className="flex flex-col items-center">
                      <VLine h={20} />
                      <OrgCard label={role} level="field" />
                    </div>
                  ))}
                </div>
              </div>

              <VLine h={28} />

              <div className="relative w-full flex justify-center">
                <div className="absolute top-0 h-px bg-border/40" style={{ left: '22%', right: '22%' }} />
                <div className="flex gap-16 justify-center">
                  {(isAr
                    ? ["المشغلون والسائقون", "الفنيون والمساعدون", "الورشة"]
                    : ["Operators & Drivers", "Technicians & Helpers", "Workshop"]
                  ).map((role) => (
                    <div key={role} className="flex flex-col items-center">
                      <VLine h={20} />
                      <OrgCard label={role} level="worker" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══ CLIENTS ═══ */}
      <AnimatedSection id="clients">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {isAr ? 'شركاؤنا' : 'SOME OF OUR'}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">
              {isAr ? 'عملاؤنا' : 'Our Clients'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isAr
                ? 'شركاء موثوقون في قطاع النفط والغاز والصناعة بالمملكة العربية السعودية.'
                : "Trusted partners across Saudi Arabia's oil, gas, and industrial sectors."}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { name: 'Saudi Aramco',          logo: '/images/crop_aramco.png' },
              { name: 'SABIC',                 logo: '/images/crop_sabic.png' },
              { name: 'Saudi Electricity Co.', logo: '/images/crop_sec.png' },
              { name: 'Petro Rabigh',          logo: '/images/crop_petrorabigh.png' },
              { name: 'Marafiq',              logo: '/images/crop_marafiq.png' },
              { name: 'Samref',               logo: '/images/crop_samref.png' },
              { name: 'SASREF',               logo: '/images/crop_sasref.png' },
              { name: 'Luberef',              logo: '/images/crop_luberef.png' },
            ].map((client) => (
              <motion.div
                key={client.name}
                variants={staggerItem}
                whileHover={{ scale: 1.04 }}
                transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              >
                <Card className="border-border bg-white hover:border-primary/40 hover:shadow-md transition-all duration-300 h-40 flex items-center justify-center overflow-hidden rounded-xl">
                  <CardContent className="flex items-center justify-center p-0 w-full h-full">
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const span = img.nextElementSibling as HTMLElement;
                        if (span) span.style.display = 'block';
                      }}
                    />
                    <span className="hidden text-sm font-bold text-primary text-center">{client.name}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* ═══ CONTACT ═══ */}
      <AnimatedSection id="contact">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.3em] flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-primary"></span>
              {T.contact.badge}
              <span className="inline-block w-10 h-px bg-primary"></span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-tight">{T.contact.title}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{T.contact.subtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Head Office */}
            <div className="bg-card border border-border hover:border-primary/40 transition-colors duration-300 p-6" style={{ clipPath: 'polygon(3% 0%, 100% 0%, 97% 100%, 0% 100%)' }}>
              <div className={`flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div className="p-3 rounded-lg bg-primary"><MapPin className="w-6 h-6 text-white" /></div>
                <span className="font-semibold text-base">{T.contact.headOffice}</span>
              </div>
              <div className={isAr ? 'text-right' : ''}>
                <p className="text-muted-foreground text-sm leading-relaxed">{CONTACT_INFO.address}</p>
                <p className="text-xs text-muted-foreground mt-2">{CONTACT_INFO.poBox}</p>
                <p className="text-xs text-primary mt-1">{CONTACT_INFO.gps}</p>
              </div>
            </div>

            {/* Mobile */}
            <div className="bg-card border border-border hover:border-primary/40 transition-colors duration-300 p-6" style={{ clipPath: 'polygon(3% 0%, 100% 0%, 97% 100%, 0% 100%)' }}>
              <div className={`flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div className="p-3 rounded-lg bg-primary"><Phone className="w-6 h-6 text-white" /></div>
                <span className="font-semibold text-base">{T.contact.mobile}</span>
              </div>
              <div className={isAr ? 'text-right' : ''}>
                <p className="text-foreground font-semibold text-lg">{CONTACT_INFO.phone}</p>
                <p className="text-xs text-muted-foreground mt-2">{T.contact.whatsapp}</p>
              </div>
            </div>

            {/* Email */}
            <div className="bg-card border border-border hover:border-primary/40 transition-colors duration-300 p-6" style={{ clipPath: 'polygon(3% 0%, 100% 0%, 97% 100%, 0% 100%)' }}>
              <div className={`flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div className="p-3 rounded-lg bg-primary"><Mail className="w-6 h-6 text-white" /></div>
                <span className="font-semibold text-base">{T.contact.email}</span>
              </div>
              <div className={isAr ? 'text-right' : ''}>
                <p className="text-foreground font-semibold">{CONTACT_INFO.email}</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className={`text-xl font-bold mb-6 uppercase tracking-wider text-muted-foreground ${isAr ? 'text-right' : ''}`}>{T.contact.emergency}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {EMERGENCY_CONTACTS.map((contact) => (
                <div key={contact.id} className="bg-card border border-destructive/30 p-4" style={{ clipPath: 'polygon(4% 0%, 100% 0%, 96% 100%, 0% 100%)' }}>
                  <div className={`flex items-center gap-2 mb-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <div className="p-1.5 rounded-md bg-destructive"><AlertCircle className="w-4 h-4 text-white" /></div>
                    <span className="font-semibold text-sm">{contact.title}</span>
                  </div>
                  <div className={isAr ? 'text-right' : ''}>
                    <p className="text-xl font-bold text-destructive">{contact.phone}</p>
                    <p className="text-xs text-muted-foreground mt-1">{contact.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Company Registration Details */}
            {/* Company Registration Details */}
            <div className="bg-card border border-border p-6" style={{ clipPath: 'polygon(2% 0%, 100% 0%, 98% 100%, 0% 100%)' }}>
              <div className={`flex items-center gap-3 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 rounded-lg bg-primary"><Building2 className="w-5 h-5 text-white" /></div>
                <span className="font-semibold text-base uppercase tracking-wide">{T.contact.registration}</span>
              </div>
              <Table>
                <TableBody>
                  {regTableRows.map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell className={`font-semibold text-muted-foreground text-sm w-48 ${isAr ? 'text-right' : ''}`}>{label}</TableCell>
                      <TableCell className={`font-mono text-sm ${isAr ? 'text-right' : ''}`}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Contact Form */}
            <div className="bg-card border border-border p-6" style={{ clipPath: 'polygon(2% 0%, 100% 0%, 98% 100%, 0% 100%)' }}>
              <p className="text-primary text-xs font-bold uppercase tracking-widest flex items-center gap-2 mb-2">
                <span className="inline-block w-4 h-px bg-primary"></span>
                {isAr ? 'تحدث إلينا' : 'LETS TALK'}
              </p>
              <h3 className="text-2xl font-extrabold mb-4">
                {isAr ? 'هل لديك مشروع قادم؟' : 'Have an upcoming project?'}
              </h3>
              <ContactForm isAr={isAr} />
            </div>
          </div>
        </div>
      </AnimatedSection>
    </Layout>
  );
}
